// ConfigContext.tsx
import React, {createContext, useContext, useState, useEffect} from "react";
import {config as initialConfig} from './portalConfig';  // Import your configuration
import type {ArtScene} from './components/ArticleArt';

export type TabItem = {
    link: string;
    label: string;
    disabled?: boolean;
    icon?: React.ElementType;
};

export type FooterColumn = {
    title: string;
    items: TabItem[];
};

export type SocialLink = {
    label: string;
    href: string;
    icon: React.ElementType;
};

type Features = {
    icon?: React.ElementType;
    title: string;
    description?: string;
}

type Cta = {
    label: string;
    link: string;
}

type Home = {
    eyebrow: string;
    title: string;
    subtitle: string;
    primaryCta: Cta;
    secondaryCta: Cta;
    body: string;
    pillars: Features[];
    method: Method;
    venturesHeading: string;
    venturesIntro: string;
}

type Method = {
    heading: string;
    intro: string;
    steps: { title: string; description: string }[];
}

export type Category = {
    categoryId: string;
    title: string;
    info?: string;
    image?: string;
    icon?: React.ElementType;
}

type Author = {
    name: string;
    avatar?: React.ElementType;
    photo?: string;
}

export type NewsCategories = {
    [key: string]: Category;
}

export type DemosCategories = {
    [key: string]: Category;
}

export type Source = {
    label: string;
    href: string;
}

export type NewsItem = {
    categoryId: string;
    assetId: string;
    date?: string;
    title: string;
    // Header visual. `image` wins when the file is present; `scene`/`tone` draw
    // the fallback artwork otherwise. See components/ArticleImage.tsx.
    image?: string;
    scene?: ArtScene;
    tone?: 'brand' | 'accent' | 'mixed';
    abstract?: string;
    intro: string;
    keyPoints: string[];
    cta: string;
    conclusion: string;
    author?: string;
    sources?: Source[];
}

export type DemosItem = {
    categoryId: string;
    assetId: string;
    date?: string;
    title: string;
    image?: string;
    abstract?: string;
    author?: string;
}


type FAQ = {
    question: string;
    answer: string;
    icon?: React.ElementType;
}

/**
 * Venture landing pages are assembled from ordered section blocks. Each block
 * kind maps to one renderer in src/components/venture/; varying the order per
 * venture is what keeps the four pages from reading as clones.
 */
export type VentureSection =
    | {
          // Long-form copy. Same convention as the rest of the config: paragraphs
          // are joined with "|" and a leading "#" marks a bold heading.
          kind: 'prose';
          title?: string;
          body: string;
      }
    | {
          kind: 'featureGrid';
          title: string;
          intro?: string;
          items: { icon?: React.ElementType; title: string; description: string }[];
      }
    | {
          kind: 'steps';
          title: string;
          intro?: string;
          items: { title: string; description: string }[];
      }
    | {
          kind: 'useCases';
          title: string;
          intro?: string;
          items: { icon?: React.ElementType; title: string; description: string }[];
      }
    | {
          kind: 'faq';
          title: string;
          items: FAQ[];
      }
    | {
          kind: 'cta';
          title: string;
          description?: string;
          cta: Cta;
      };

export type Venture = {
    slug: string;
    name: string;
    route: string;
    tagline: string;
    summary: string;
    icon: React.ElementType;
    status?: string;      // e.g. "Launching soon" — omitted when generally available
    // The venture's own live site. Omitted when there is nothing to link to
    // yet; never point at a domain that does not serve the product.
    // `action` overrides the button text — some of these open a marketing site,
    // others drop straight into the application's sign-in.
    website?: {label: string; href: string; action?: string};
    sections: VentureSection[];
}

type Section = {
    title: string;
    paragraphs: string[];
}

export type ConsultingOffering = {
    title: string;
    description: string;
    icon: React.ElementType;
    // Rendered as a bulleted list above the description — the buyer reads the
    // outcome before the explanation.
    valuePoints: string[];
}

export type Leader = {
    name: string;
    title: string;
    photo: string;
    intro: string;
    // "Organisation|what they did there" — split on the pipe when rendering.
    experience: string[];
}

export type Leadership = {
    heading: string;
    intro: string;
    people: Leader[];
}

export type Consulting = {
    heading: string;
    intro: string;
    offerings: ConsultingOffering[];
}

type Privacy ={
    lastUpdated: string;
    intro: string[];
    sections: Section[];
}

type Contact = {
    title: string;
    description: string;
    icon: React.ElementType;
}

type ConfigType = {
    navigationLinks: TabItem[];
    footerLinks: FooterColumn[];
    footerNote: string;
    socialLinks: SocialLink[];
    home: Home;
    ventures: Venture[];
    consulting: Consulting;
    leadership: Leadership;
    authors: Author[];
    newsCategories: NewsCategories;
    demosCategories: DemosCategories;
    news: NewsItem[];
    demos: DemosItem[];
    faqs: FAQ[];
    privacy: Privacy;
    contacts: Contact[];
};

export const ConfigContext = createContext<ConfigType | null>(null);

export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (!context) throw new Error("useConfig must be used within a ConfigProvider");
    return context;
};

interface ConfigProviderProps {
    children: React.ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({children}) => {
    const [config, setConfig] = useState<ConfigType>(initialConfig); // This should now be correctly typed

    useEffect(() => {
        // Here you can fetch remote config and merge or replace it
        // For example, this could be replaced with remote fetching logic
        setConfig(initialConfig);
    }, []);

    return (
        <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
    );
};
